import { useEffect, useRef, useState } from 'react';
import { Icon, IconButton } from '../ds';
import { formatDateShortJa } from '../lib/time';
import type { Photo } from '../lib/types';

/**
 * Album tiles.
 *
 * The prototype used `image-slot.js`, a design-tool scaffold that stored a
 * dropped image in a JSON sidecar next to the HTML file. Here photos are real
 * uploads to Cloud Storage, so the tile is either a photo or an invitation to
 * add one — and grandparents, who are read-only, only ever see the former.
 */

export function PhotoTile({
  photo,
  height,
  radius = 18,
  onOpen,
}: {
  photo: Photo;
  height: number;
  radius?: number;
  onOpen: (photo: Photo) => void;
}) {
  const [loaded, setLoaded] = useState(false);

  return (
    <button
      type="button"
      onClick={() => onOpen(photo)}
      style={{
        position: 'relative',
        display: 'block',
        width: '100%',
        height,
        padding: 0,
        border: 'none',
        borderRadius: radius,
        overflow: 'hidden',
        cursor: 'pointer',
        background: 'var(--surface-sunken)',
      }}
    >
      <img
        src={photo.url}
        alt={photo.caption ?? '思い出の写真'}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          opacity: loaded ? 1 : 0,
          transition: 'opacity var(--duration-normal) var(--ease-standard)',
        }}
      />
      {photo.caption ? (
        <span
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '18px 12px 9px',
            textAlign: 'left',
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            color: '#fff',
            // A flat scrim, not a decorative gradient: the design system rules
            // out gradients as styling, but caption legibility needs a wash.
            background: 'linear-gradient(to top, rgba(28,25,20,0.62), rgba(28,25,20,0))',
            textShadow: '0 1px 2px rgba(0,0,0,0.35)',
          }}
        >
          {photo.caption}
        </span>
      ) : null}
    </button>
  );
}

/** Empty tile that opens the file picker. */
export function AddPhotoTile({
  height,
  radius = 18,
  placeholder = '写真を追加',
  onFiles,
  busy = false,
}: {
  height: number;
  radius?: number;
  placeholder?: string;
  onFiles: (files: File[]) => void;
  busy?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
          if (files.length) onFiles(files);
        }}
        disabled={busy}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          width: '100%',
          height,
          borderRadius: radius,
          border: `1.5px dashed ${dragOver ? 'var(--brand)' : 'var(--border-strong)'}`,
          background: dragOver ? 'var(--brand-subtle)' : 'var(--surface-sunken)',
          color: 'var(--text-subtle)',
          cursor: busy ? 'progress' : 'pointer',
          transition: 'background var(--duration-fast) var(--ease-standard), border-color var(--duration-fast) var(--ease-standard)',
          padding: 12,
        }}
      >
        <Icon name={busy ? 'Loader' : 'ImagePlus'} size={22} style={busy ? { animation: 'spin 1s linear infinite' } : undefined} />
        <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, textAlign: 'center' }}>
          {busy ? 'アップロード中…' : placeholder}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) onFiles(files);
          e.target.value = '';
        }}
      />
    </>
  );
}

/** Full-screen viewer, with delete for parents. */
export function PhotoLightbox({
  photo,
  onClose,
  onDelete,
}: {
  photo: Photo;
  onClose: () => void;
  onDelete?: (photo: Photo) => void;
}) {
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="写真"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 70,
        background: 'rgba(16, 14, 11, 0.94)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'scrimIn .18s ease',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          padding: 'calc(12px + env(safe-area-inset-top, 0px)) 14px 12px',
          color: '#fff',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>
            {photo.caption ?? 'おもいで'}
          </div>
          <div style={{ fontSize: 11, opacity: 0.7, fontFamily: 'var(--font-mono)' }}>
            {formatDateShortJa(photo.takenAt)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {onDelete ? (
            <IconButton
              icon="Trash2"
              label="この写真を削除"
              variant="ghost"
              onClick={() => setConfirming(true)}
              style={{ color: '#fff' }}
            />
          ) : null}
          <IconButton icon="X" label="閉じる" variant="ghost" onClick={onClose} style={{ color: '#fff' }} />
        </div>
      </div>

      <div
        onClick={onClose}
        style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
      >
        <img
          src={photo.url}
          alt={photo.caption ?? '思い出の写真'}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 'var(--radius-sm)' }}
        />
      </div>

      {confirming && onDelete ? (
        <div
          style={{
            padding: '16px 18px calc(20px + env(safe-area-inset-bottom, 0px))',
            background: 'var(--surface)',
            borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
            animation: 'sheetUp .24s cubic-bezier(0.32,0.72,0.36,1)',
          }}
        >
          <div className="cjk" style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', marginBottom: 12 }}>
            この写真を削除しますか？もどせません。
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              style={{
                flex: 1,
                height: 'var(--control-md)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-strong)',
                background: 'transparent',
                color: 'var(--text)',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              やめる
            </button>
            <button
              type="button"
              onClick={() => {
                onDelete(photo);
                onClose();
              }}
              style={{
                flex: 1,
                height: 'var(--control-md)',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: 'var(--danger)',
                color: 'var(--danger-on)',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              削除する
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
